import {
  LocationID,
  Street,
  ResidentialBuilding,
  AdminBuilding,
  SpecialBuilding,
  Building,
  Room,
  Finding,
  KeyCard,
  ItemID,
  Rarity,
  Location,
  City,
  IWorldRepository,
  IItemRepository,
  LootGeneratorService,
} from '@stalker/core';
import { v4 } from 'uuid';
import { ITEM_DEFINITIONS } from '#shared/config/items.js';
import { WORLD_CONFIG, LootConfig } from '#app/world-data/world-config.js';
import type { LocationDef } from '#entities/location/index.js';

class WebWorldRepository implements IWorldRepository {
  constructor(private locations: Record<string, LocationDef>) {}
  getLockedBuildingsCount() {
    return Object.values(this.locations).filter(l => l.core.lock !== null).length;
  }
  getTotalResidentialCount() {
    return Object.values(this.locations).filter(l => l.type === 'residential').length;
  }
  getStalkerCount() { return 1; }
  getPatrolCount() { return 1; }
  getDetentionsCount() { return 0; }
  isParentSquated(locationId: LocationID): boolean {
    const node = this.locations[locationId.toString()];
    if (!node || !node.parentId) return false;
    const parentNode = this.locations[node.parentId];
    return parentNode?.core.lock !== null;
  }
}

class WebItemRepository implements IItemRepository {
  constructor(private locations: Record<string, LocationDef>) {}
  countKeyCardsForLocation(locationId: LocationID) {
    let count = 0;
    Object.values(this.locations).forEach(l => {
      l.core.getFindings().forEach(f => {
        if (f instanceof KeyCard && f.locationId && String(f.locationId) === String(locationId)) {
          count++;
        }
      });
    });
    return count;
  }
}



function createBuilding(id: string, type: string, radiation: number): Building {
  const locId = new LocationID(id);
  switch (type) {
    case 'residential': return new ResidentialBuilding(locId, radiation);
    case 'school': return new AdminBuilding(locId, radiation);
    case 'hospital':
    case 'military':
    case 'factory':
    case 'bunker': return new SpecialBuilding(locId, radiation);
    default: return new Building(locId, radiation);
  }
}

function assignLootToRoom(room: Room, lootRules: LootConfig[]) {
  lootRules.forEach(rule => {
    const count = Math.floor(Math.random() * (rule.max - rule.min + 1)) + rule.min;
    const possibleItems = ITEM_DEFINITIONS.filter(i => i.collection === rule.collection);

    for (let i = 0; i < count; i++) {
      if (possibleItems.length === 0) break;
      const itemDef = possibleItems[Math.floor(Math.random() * possibleItems.length)];

      const currentInventory = [...room.getFindings()];
      currentInventory.push(new Finding({
        id: new ItemID(v4()),
        typeId: itemDef.id,
        weight: 0.1 + Math.random() * 0.5,
        rarity: itemDef.rarity || Rarity.Common,
        category: itemDef.category,
        collectionId: itemDef.collection
      }));
      room.setInventory(currentInventory);
    }
  });
}

export const createWorld = () => {
  const locations: Record<string, LocationDef> = {};

  // 1. Создаем Город (Корень)
  const cityId = WORLD_CONFIG.id;
  const cityCore = new City(new LocationID(cityId), 0);
  locations[cityId] = {
    id: cityId,
    core: cityCore,
    type: 'city',
    name: WORLD_CONFIG.name,
    description: WORLD_CONFIG.description,
    dangerLevel: 0,
    children: []
  };

  WORLD_CONFIG.streets.forEach(sConfig => {
    // 2. Создаем Улицу
    const streetCore = new Street(new LocationID(sConfig.id), 0.01);

    locations[sConfig.id] = {
      id: sConfig.id,
      core: streetCore,
      type: 'street',
      name: sConfig.name,
      description: sConfig.description,
      dangerLevel: sConfig.dangerLevel,
      parentId: cityId,
      children: [],
      imageUrl: sConfig.imageUrl,
      soundPreset: sConfig.soundPreset
    };
    locations[cityId].children.push(sConfig.id);

    sConfig.buildings.forEach(bConfig => {
      // 3. Создаем Здание
      const isSafeHouse = bConfig.type === 'residential';
      const radiation = isSafeHouse ? -0.001 : bConfig.dangerLevel * 0.005;
      const coreBuilding = createBuilding(bConfig.id, bConfig.type, radiation);

      locations[bConfig.id] = {
        id: bConfig.id,
        core: coreBuilding,
        type: bConfig.type,
        name: bConfig.name,
        description: bConfig.description,
        dangerLevel: bConfig.dangerLevel,
        parentId: sConfig.id,
        children: [],
        imageUrl: bConfig.imageUrl,
        soundPreset: bConfig.soundPreset
      };
      locations[sConfig.id].children.push(bConfig.id);

      // 4. Создаем Комнаты
      bConfig.rooms.forEach(rConfig => {
        const fullRoomId = `${bConfig.id}_${rConfig.id}`;
        const roomCore = new Room(new LocationID(fullRoomId), radiation);

        locations[fullRoomId] = {
          id: fullRoomId,
          core: roomCore,
          type: 'room',
          name: rConfig.name,
          description: rConfig.description || `Внутреннее помещение в ${bConfig.name}.`,
          dangerLevel: bConfig.dangerLevel,
          parentId: bConfig.id,
          children: [],
          soundPreset: rConfig.soundPreset
        };
        locations[bConfig.id].children.push(fullRoomId);

        // Раскладываем лут по комнате
        if (rConfig.loot) {
          assignLootToRoom(roomCore, rConfig.loot);
        }
      });
    });
  });

  const lootGenerator = new LootGeneratorService(
    new WebWorldRepository(locations),
    new WebItemRepository(locations)
  );

  lootGenerator.setTemplates(ITEM_DEFINITIONS.map(d => ({
    typeId: d.id,
    category: d.category,
    rarity: d.rarity,
    weight: d.weight
  })));

  return {
    city: locations[cityId],
    street: locations[WORLD_CONFIG.streets[0].id].core, // Для обратной совместимости
    locations,
    lootGenerator
  };
};

export type World = ReturnType<typeof createWorld>;
